import { CVRMailbox, BaseMessage } from './../utils/vrmailbox';
import { ETranslationBehavior } from './elasticheadtransform';
import { EPanelSampler, EPanelStereoscopy, Panel } from './panel';
import { ERenderModelCulling } from './rendermodel';
import { Color, EqualsColor } from './../utils/transform';

export interface SgNodeProperties
{
	id?: string;
	sgid?: number;
	translation?: number[];
	rotation?: number[];
	scale?: number[];
	color?: number[] | string;
	opacity?: number;
	strength?: number;
	wireframe?: boolean;
	solid?: boolean;
	culling?: ERenderModelCulling;
	"no-depth-write"?: boolean;
	"no-depth-test"?: boolean;
	"parent-path"?: string;
	"parent-origin"?: string;
	"parent-id"?: string;
	"visible-0dof"?: boolean;
	"visible-3dof"?: boolean;
	"visible-6dof"?: boolean;
	"start-angle-threshold"?: number;
	"stop-angle-threshold"?: number;
	"ease-in-time"?: number;
	"ease-in-power"?: number;
	"ease-out-angle-threshold"?: number;
	"ease-out-power"?: number;
	"min-angular-velocity"?: number;
	"max-angular-velocity"?: number;
	"lock-to-horizon"?: boolean;
	"translation-behavior"?: ETranslationBehavior;
	source?: string;
	overlay_handle?: string;
	key?: string;
	uv_min?: number[];
	uv_max?: number[];
	width?: number;
	height?: number;
	"target-dpi-panel-id"?: string;
	"target-dpi-multiplier"?: number;
	curvature?: number;
	"curvature-origin-id"?: string;
	interactive?: boolean;
	scrollable?: boolean;
	modal?: boolean;
	debug_name?: string;
	sampler?: EPanelSampler;
	reflect?: number;
	stereoscopy?: EPanelStereoscopy;
	"embedded-uv-index"?: number;
	origin?: number[];
	"target-id"?: string;
	"source-id"?: string;
	"texture-id"?: string;
	"sort-order"?: number;
	"sort-depth-bias"?: number;
	thickness?: number;
	"start-buffer"?: number;
	"end-buffer"?: number;
	"source-distance"?: number;
	"target-limit"?: number;
	offset?: number[];
	"offscreen-z-depth"?: number;
	"off-axis-limit"?: number;
	"transition-limit"?: number;
	"anchor-u"?: number;
	"anchor-v"?: number;
	tab_name?: string;
	mountable_id?: string;
	icon_uri?: string;
	icon_overlay_key?: string;
	summon_overlay_key?: string;
	owning_overlay_key?: string;
	rendermodel_component_device_index?: number;
	rendermodel_component_name?: string;
}


function GetAttributeFloatArray( domNode: HTMLElement, sAttrName: string ): number[]
{
	let attr = domNode.getAttribute( sAttrName );
	if ( attr && attr.length > 0 )
	{
		// some of the nodes are outputing the literal string "undefined". Oops.
		if ( attr == "undefined" )
		{
			return undefined;
		}

		let results: number[] = [];
		for ( let entry of attr.split( " " ) )
		{
			if ( entry.length == 0 )
				continue;

			results.push( parseFloat( entry ) );
		}

		if ( results.length > 0 )
		{
			return results;
		}
	}
	return undefined;
}


function GetAttributeFloat( domNode: HTMLElement, sAttrName: string ): number
{
	let attr = domNode.getAttribute( sAttrName );
	if ( attr && attr.length > 0 )
	{
		return parseFloat( attr );
	}
	return undefined;
}

function GetAttributeInt( domNode: HTMLElement, sAttrName: string ): number
{
	let attr = domNode.getAttribute( sAttrName );
	if ( attr && attr.length > 0 )
	{
		return parseInt( attr );
	}
	return undefined;
}

function GetAttributeBool( domNode: HTMLElement, sAttrName: string ): boolean
{
	let attr = domNode.getAttribute( sAttrName );
	if ( attr && attr.length > 0 )
	{
		return attr == "true" || attr.length > 0 && parseInt( attr ) != 0 && !isNaN( parseInt( attr ) );
	}
	return undefined;
}

function GetAttributeString( domNode: HTMLElement, sAttrName: string ): string
{
	let attr = domNode.getAttribute( sAttrName );
	if ( attr && attr.length > 0 )
	{
		return attr;
	}
	return undefined;
}

function GetPossiblyQualifiedAttributeID( domNode: HTMLElement, sAttrName: string ): string
{
	return FullyQualifyIDIfNeeded( GetAttributeString( domNode, sAttrName ) );
}


export interface SgNode
{
	type: string;
	children?: SgNode[];
	properties?: SgNodeProperties;
}

// These are non-exported, on purpose
interface SgRootProperties
{
	relatchDashboardTransform: boolean;
	sceneCompositeMult?: number;
}

// These are non-exported, on purpose
interface SgRootNode extends SgNode
{
	rootproperties?: SgRootProperties;
}

export function CreateSgNode( type: string, domNode: HTMLElement ): SgNode
{
	let node: SgNode = { type };
	node.properties = {};
	if ( domNode.id )
	{
		node.properties.id = FullyQualifyIDIfNeeded( domNode.id );
	}

	node.properties.sgid = GetAttributeInt( domNode, "sgid" );
	return node;
}

export function GetOwningOverlayHandle(): string
{
	return VRHTML?.VROverlay.ThisOverlayHandle();
}

export type BuildSgNodeReturn = [TraversalState /* mutated child state */, SgNode /* translated node*/];

export type BuildSgNode = ( traversalState: TraversalState, domNode: HTMLElement ) => BuildSgNodeReturn;

function TranslateNode( traversalState: TraversalState, domNode: HTMLElement ): BuildSgNodeReturn
{
	let buildNode: BuildSgNode = ( domNode as any ).buildNode;
	if ( buildNode )
	{
		return buildNode( traversalState, domNode );
	}

	let mutatedState: TraversalState = { ...traversalState };
	let result: SgNode = null;
	let elementType = domNode.nodeName.toUpperCase();
	switch ( elementType )
	{
		case "VSG-TRANSFORM":
			result = CreateSgNode( "transform", domNode );
			result.properties.translation = GetAttributeFloatArray( domNode, "translation" );
			result.properties.rotation = GetAttributeFloatArray( domNode, "rotation" );
			result.properties.scale = GetAttributeFloatArray( domNode, "scale" );
			result.properties["parent-path"] = GetAttributeString( domNode, "parent-path" );
			result.properties["parent-origin"] = GetAttributeString( domNode, "parent-origin" );
			result.properties["parent-id"] = GetPossiblyQualifiedAttributeID( domNode, "parent-id" );
			break;

		case "VSG-TRACKING-STATE-VISIBILITY":
			result = CreateSgNode( "trackingstatevisibility", domNode );
			result.properties["visible-0dof"] = GetAttributeBool( domNode, "visible-0dof" );
			result.properties["visible-3dof"] = GetAttributeBool( domNode, "visible-3dof" );
			result.properties["visible-6dof"] = GetAttributeBool( domNode, "visible-6dof" );
			break;

		case "VSG-ELASTIC-HEAD-TRANSFORM":
			result = CreateSgNode( "elasticheadtransform", domNode );

			result.properties["start-angle-threshold"] = GetAttributeFloat( domNode, "start-angle-threshold" );
			result.properties["stop-angle-threshold"] = GetAttributeFloat( domNode, "stop-angle-threshold" );
			result.properties["ease-in-time"] = GetAttributeFloat( domNode, "ease-in-time" );
			result.properties["ease-in-power"] = GetAttributeFloat( domNode, "ease-in-power" );
			result.properties["ease-out-angle-threshold"] = GetAttributeFloat( domNode, "ease-out-angle-threshold" );
			result.properties["ease-out-power"] = GetAttributeFloat( domNode, "ease-out-power" );
			result.properties["min-angular-velocity"] = GetAttributeFloat( domNode, "min-angular-velocity" );
			result.properties["max-angular-velocity"] = GetAttributeFloat( domNode, "max-angular-velocity" );
			result.properties["lock-to-horizon"] = GetAttributeBool( domNode, "lock-to-horizon" );
			result.properties["translation-behavior"] = GetAttributeInt( domNode, "translation-behavior" );
			break;

		case "VSG-LINE":
			result = CreateSgNode( "line", domNode );

			result.properties["target-id"] = GetPossiblyQualifiedAttributeID( domNode, "target-id" );
			result.properties.thickness = GetAttributeFloat( domNode, "thickness" );
			result.properties["start-buffer"] = GetAttributeFloat( domNode, "start-buffer" );
			result.properties["end-buffer"] = GetAttributeFloat( domNode, "end-buffer" );
			break;

		case "VSG-LINE-CONSTRAINED-TRANSFORM":
			result = CreateSgNode( "line-constrained-transform", domNode );

			result.properties["target-id"] = GetPossiblyQualifiedAttributeID( domNode, "target-id" );
			result.properties["source-id"] = GetPossiblyQualifiedAttributeID( domNode, "source-id" );
			result.properties["source-distance"] = GetAttributeFloat( domNode, "source-distance" );
			result.properties["target-limit"] = GetAttributeFloat( domNode, "target-limit" );
			break;

		case "VSG-CALLOUT-TRANSFORM":
			result = CreateSgNode( "callout-transform", domNode );
			result.properties.offset = GetAttributeFloatArray( domNode, "offset" );
			break;

		case "VSG-HEAD-FACING-TRANSFORM":
			result = CreateSgNode( "head-facing-transform", domNode );
			break;

		case "VSG-PIN-TO-VIEW-TRANSFORM":
			result = CreateSgNode( "pin-to-view-transform", domNode );

			result.properties["offscreen-z-depth"] = GetAttributeFloat( domNode, "offscreen-z-depth" );
			result.properties["off-axis-limit"] = GetAttributeFloat( domNode, "off-axis-limit" );
			result.properties["transition-limit"] = GetAttributeFloat( domNode, "transition-limit" );
			break;
	}

	return [mutatedState, result];
}


export interface TraversalState
{
	currentPanel: Panel;
	bShouldAbort: boolean;
}

function TraverseNode( traversalState: TraversalState, domNode: HTMLElement ): SgNode[]
{
	let [childState, node] = TranslateNode( traversalState, domNode );

	let children: SgNode[] = [];
	for ( let childIndex = 0; childIndex < domNode.children.length; childIndex++ )
	{
		let domChild = domNode.children.item( childIndex );
		if ( domChild.children )
		{
			let childNodes = TraverseNode( childState, domChild as HTMLElement );
			if ( childNodes )
			{
				children = children.concat( childNodes );
			}
		}
	}

	if ( childState.bShouldAbort )
	{
		return null;
	}

	if ( node )
	{
		if ( children.length > 0 )
		{
			node.children = children;
		}
		return [node];
	}
	else if ( children.length > 0 )
	{
		if ( children.length == 1 )
		{
			return children;
		}
		else
		{
			// Implicit "empty" node, translates to a sg::CEmpty on the compositor.
			return [{ children } as any];
		}
	}
	else
	{
		return null;
	}
}

function TranslateSceneGraph( domNode: HTMLElement, rootproperties: SgRootProperties ): SgRootNode
{
	const initialTraversalState: TraversalState = {
		currentPanel: null,
		bShouldAbort: false,
	};

	const rootNodes = TraverseNode( initialTraversalState, domNode );

	return { type: "root", rootproperties, children: rootNodes };
}

interface MsgUpdateSceneGraph extends BaseMessage
{
	owning_overlay?: string;
	owning_overlay_key?: string;
	scene_graph: SgNode;
	retired_sgids?: number[];
}

let g_RetiredSgids: number[] = [];
let g_SceneGraphDirtyTimer: number = null;
let g_SGMailbox: CVRMailbox = null;
let g_OwningOverlayKey: string;
let g_OwningAppElement: HTMLElement;
let g_WebSecret: string;
let g_RelatchDashboardTransform: boolean;
let g_SceneCompositeMult: number = null;

export function SetOwningOverlayKey( sOwningOverlayKey: string, sgAppElement: HTMLElement, sWebSecret: string )
{
	g_OwningOverlayKey = sOwningOverlayKey;
	g_OwningAppElement = sgAppElement;
	g_WebSecret = sWebSecret;
	console.log( "Setting owning overlay key to " + sOwningOverlayKey );
}

export function IsIDFullyQualified( sPossiblyQualifiedID: string )
{
	return ( sPossiblyQualifiedID && sPossiblyQualifiedID.includes( "::" ) );
}

export function ConstructFullyQualifiedID( sOwningOverlayKey: string, sID: string )
{
	return sOwningOverlayKey + "::" + sID;
}

export function FullyQualifyIDIfNeeded( sPossiblyQualifiedID: string )
{
	if ( !sPossiblyQualifiedID )
	{
		return null;
	}

	if ( IsIDFullyQualified( sPossiblyQualifiedID ) )
	{
		return sPossiblyQualifiedID;
	}
	else
	{
		return ConstructFullyQualifiedID( GetOwningOverlayKey(), sPossiblyQualifiedID );
	}
}

export function GetOwningOverlayKey()
{
	return VRHTML?.VROverlay.ThisOverlayKey() ?? g_OwningOverlayKey;
}

export function RetireSgid( sgid: number )
{
	g_RetiredSgids.push( sgid );
	MarkSceneGraphDirty();
}

export function RelatchDashboardTransform()
{
	g_RelatchDashboardTransform = true;
}

export function SetSceneCompositeColorCorrection( mult: number )
{
	if ( mult != g_SceneCompositeMult )
	{
		g_SceneCompositeMult = mult;
		MarkSceneGraphDirty();
	}
}

export function MarkSceneGraphDirty()
{
	if ( !g_SceneGraphDirtyTimer )
	{
		g_SceneGraphDirtyTimer = window.setTimeout(
			async () =>
			{
				let rootDomNode = document.body;
				if ( g_OwningAppElement )
				{
					rootDomNode = g_OwningAppElement;
				}

				let sgRootProperties: SgRootProperties = {
					relatchDashboardTransform: g_RelatchDashboardTransform,
					sceneCompositeMult: g_SceneCompositeMult,
				};

				let sgRoot: SgRootNode = TranslateSceneGraph( rootDomNode, sgRootProperties );

				if ( !g_SGMailbox )
				{
					console.log( "Initializing sg_mailbox" );
					g_SGMailbox = new CVRMailbox();
					await g_SGMailbox.Init( "sg_mailbox", g_WebSecret );
				}

				//console.log( "Using mailbox to update scene graph for " + g_OwningOverlayKey );
				let msg: MsgUpdateSceneGraph =
					{
						type: "update_scene_graph",
						owning_overlay_key: GetOwningOverlayKey(),
						scene_graph: sgRoot,
						retired_sgids: g_RetiredSgids,
					};
				g_SGMailbox.SendMessage( "vrcompositor_systemlayer", msg );

				g_SceneGraphDirtyTimer = null;
				g_RetiredSgids = [];
				g_RelatchDashboardTransform = false;
			},
			0,
		);
	}
}

